package one.devsbox.www.ui.locations.edit

import androidx.compose.animation.Crossfade
import androidx.compose.foundation.*
import androidx.compose.foundation.layout.*
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.*
import androidx.compose.runtime.*
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import compose.assets.Svg
import compose.assets.svg.ContentSaveOutline
import compose.assets.svg.DeleteOutline
import kotlinx.datetime.LocalTime
import one.devsbox.shared.msg.MessageBody
import one.devsbox.www.provider.LocalDisplayProvider
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.ui.widgets.ContentActions
import one.devsbox.www.ui.widgets.FullscreenErrorUI
import one.devsbox.www.ui.widgets.FullscreenLoadingUI
import one.devsbox.www.ui.widgets.LabelSection
import one.devsbox.www.ui.widgets.dowToStringLong
import one.devsbox.www.www_portal.generated.resources.*
import org.jetbrains.compose.resources.stringResource
import tk.mallumo.compose.navigation.ComposableNavNode
import tk.mallumo.utils.DataState

@Composable
@ComposableNavNode(ArgsId::class)
fun LocationEUI() {
    with(LocationECtx.get()) {
        ContentLocationEUI()
    }
}

@Composable
context(ctx: LocationECtx)
private fun ContentLocationEUI() {
    Box(modifier = Modifier.fillMaxSize().background(MaterialTheme.colorScheme.background)) {
        Crossfade(targetState = ctx.vm.state) { state ->
            when (state) {
                is DataState.Idle -> Unit
                is DataState.Loading -> FullscreenLoadingUI()
                is DataState.Error -> {
                    FullscreenErrorUI(
                        message = state.message,
                        onBack = { ctx.action.back() },
                        onReload = { ctx.action.reload() }
                    )
                }

                is DataState.Result -> Box(modifier = Modifier.fillMaxSize()) {
                    SuccessUI()
                    ContentActions(
                        contentActions = ctx.vm.contentActions,
                        colorInactive = ctx.colorSecondaryContainer,
                        colorActive = ctx.colorPrimaryContainer
                    )
                }
            }
        }

        ctx.vm.warningMessage?.let { msg ->
            AlertDialog(
                onDismissRequest = { ctx.action.clearWarning() },
                title = { Text(stringResource(Res.string.ui_devices_edit_deviceeui_error)) },
                text = { Text(msg) },
                confirmButton = {
                    TextButton(onClick = { ctx.action.clearWarning() }) {
                        Text(stringResource(Res.string.ui_devices_edit_deviceeui_ok))
                    }
                }
            )
        }
    }
}

@Composable
context(ctx: LocationECtx)
private fun SuccessUI() {
    BoxWithConstraints(
        modifier = Modifier
            .fillMaxSize()
            .padding(16.dp)
    ) {

        val isWide = maxWidth >= 1000.dp
        if (isWide) {
            Row(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
            ) {
                // Left Column: Name and Address
                Column(modifier = Modifier.weight(1f)) {
                    LocationNameAndAddressSection()
                    Spacer(modifier = Modifier.height(250.dp))
                }

                Spacer(Modifier.width(48.dp))

                // Right Column: Opening Hours
                Column(modifier = Modifier.weight(2f)) {
                    LocationOpeningHoursSection()
                    Spacer(modifier = Modifier.height(250.dp))
                }
            }
        } else {
            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(rememberScrollState())
            ) {
                // Name and Address
                LocationNameAndAddressSection()

                Spacer(Modifier.height(16.dp))

                // Opening Hours
                LocationOpeningHoursSection()
                Spacer(modifier = Modifier.height(250.dp))
            }
        }
    }
}

@Composable
context(ctx: LocationECtx)
private fun LocationNameAndAddressSection() {
    LabelSection(stringResource(Res.string.ui_locations_edit_label_name))
    Spacer(Modifier.height(8.dp))
    OutlinedTextField(
        value = ctx.vm.name,
        onValueChange = { ctx.vm.name = it },
        modifier = Modifier.fillMaxWidth(),
        textStyle = MaterialTheme.typography.headlineSmall.copy(
            fontWeight = FontWeight.Bold,
            color = MaterialTheme.colorScheme.onSurface
        ),
        colors = OutlinedTextFieldDefaults.colors(
            focusedContainerColor = Color.Transparent,
            unfocusedContainerColor = Color.Transparent,
            focusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
        ),
        shape = RoundedCornerShape(0.dp)
    )

    Spacer(Modifier.height(16.dp))

    LabelSection(stringResource(Res.string.ui_locations_edit_label_address))
    Spacer(Modifier.height(8.dp))
    OutlinedTextField(
        value = ctx.vm.address,
        onValueChange = { ctx.vm.address = it },
        modifier = Modifier.fillMaxWidth(),
        placeholder = {
            Text(
                stringResource(Res.string.ui_locations_edit_placeholder_address),
                color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.6f)
            )
        },
        textStyle = MaterialTheme.typography.bodyLarge.copy(color = MaterialTheme.colorScheme.onSurface),
        colors = OutlinedTextFieldDefaults.colors(
            focusedContainerColor = Color.Transparent,
            unfocusedContainerColor = Color.Transparent,
            focusedBorderColor = MaterialTheme.colorScheme.outlineVariant,
            unfocusedBorderColor = MaterialTheme.colorScheme.outlineVariant
        ),
        shape = RoundedCornerShape(0.dp)
    )
}

@Composable
context(ctx: LocationECtx)
private fun LocationOpeningHoursSection() {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.fillMaxWidth()
    ) {
        HorizontalDivider(
            modifier = Modifier.weight(1f),
            color = MaterialTheme.colorScheme.outlineVariant
        )
        LabelSection(stringResource(Res.string.ui_locations_edit_label_opening_hours))
        HorizontalDivider(
            modifier = Modifier.weight(1f),
            color = MaterialTheme.colorScheme.outlineVariant
        )
    }

    Spacer(Modifier.height(16.dp))

    (1..7).forEach { dow ->
        DayOpeningHoursItem(dow)
        Spacer(Modifier.height(16.dp))
    }
}

@Composable
context(ctx: LocationECtx)
private fun DayOpeningHoursItem(dow: Int) {
    val dayRanges = ctx.vm.openingHours.filter { it.dow == dow }
    val isEnabled = dayRanges.isNotEmpty()
    val dp = LocalDisplayProvider.current.state

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .border(
                width = 1.dp,
                color = if (isEnabled) MaterialTheme.colorScheme.outlineVariant else MaterialTheme.colorScheme.outlineVariant.copy(
                    alpha = 0.3f
                ),
                shape = RoundedCornerShape(0.dp)
            )
            .background(Color.Transparent)
            .padding(16.dp)
    ) {
        if (dp.smallScreen) {
            Column(
                modifier = Modifier.fillMaxWidth(),
                verticalArrangement = Arrangement.spacedBy(12.dp)
            ) {
                Row(
                    verticalAlignment = Alignment.CenterVertically,
                    modifier = Modifier.fillMaxWidth()
                ) {
                    Switch(
                        checked = isEnabled,
                        onCheckedChange = { ctx.action.toggleDay(dow, it) },
                        colors = SwitchDefaults.colors(
                            checkedThumbColor = MaterialTheme.colorScheme.onPrimary,
                            checkedTrackColor = MaterialTheme.colorScheme.primary,
                            uncheckedThumbColor = MaterialTheme.colorScheme.outline,
                            uncheckedTrackColor = MaterialTheme.colorScheme.surfaceVariant
                        )
                    )
                    Spacer(Modifier.width(16.dp))
                    Text(
                        text = stringResource(dowToStringLong(dow)),
                        fontWeight = FontWeight.Bold,
                        fontSize = 14.sp,
                        color = if (isEnabled) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant,
                        modifier = Modifier.weight(1f)
                    )
                    if (isEnabled) {
                        OutlinedButton(
                            onClick = { ctx.action.addRange(dow) },
                            shape = RoundedCornerShape(4.dp),
                            contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                            border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
                        ) {
                            Text(
                                stringResource(Res.string.ui_locations_edit_btn_add_range),
                                fontSize = 10.sp,
                                color = MaterialTheme.colorScheme.onSurfaceVariant
                            )
                        }
                    }
                }

                if (isEnabled) {
                    Column(
                        modifier = Modifier
                            .fillMaxWidth()
                            .padding(start = 8.dp),
                        verticalArrangement = Arrangement.spacedBy(8.dp)
                    ) {
                        dayRanges.forEach { range ->
                            TimeRangeRow(range)
                        }
                    }
                } else {
                    Text(
                        stringResource(Res.string.ui_locations_edit_halted),
                        fontSize = 10.sp,
                        color = MaterialTheme.colorScheme.outline,
                        modifier = Modifier.fillMaxWidth().padding(start = 66.dp),
                        textAlign = androidx.compose.ui.text.style.TextAlign.Start
                    )
                }
            }
        } else {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                modifier = Modifier.fillMaxWidth()
            ) {
                Switch(
                    checked = isEnabled,
                    onCheckedChange = { ctx.action.toggleDay(dow, it) },
                    colors = SwitchDefaults.colors(
                        checkedThumbColor = MaterialTheme.colorScheme.onPrimary,
                        checkedTrackColor = MaterialTheme.colorScheme.primary,
                        uncheckedThumbColor = MaterialTheme.colorScheme.outline,
                        uncheckedTrackColor = MaterialTheme.colorScheme.surfaceVariant
                    )
                )
                Spacer(Modifier.width(16.dp))
                Text(
                    text = stringResource(dowToStringLong(dow)),
                    fontWeight = FontWeight.Bold,
                    fontSize = 14.sp,
                    color = if (isEnabled) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant,
                    modifier = Modifier.width(100.dp)
                )

                if (isEnabled) {
                    Column(modifier = Modifier.weight(1f)) {
                        dayRanges.forEach { range ->
                            TimeRangeRow(range)
                        }
                    }
                    Spacer(Modifier.width(16.dp))
                    OutlinedButton(
                        onClick = { ctx.action.addRange(dow) },
                        shape = RoundedCornerShape(4.dp),
                        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
                        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant)
                    ) {
                        Text(
                            stringResource(Res.string.ui_locations_edit_btn_add_range),
                            fontSize = 10.sp,
                            color = MaterialTheme.colorScheme.onSurfaceVariant
                        )
                    }
                } else {
                    Text(
                        stringResource(Res.string.ui_locations_edit_halted),
                        fontSize = 10.sp,
                        color = MaterialTheme.colorScheme.outline,
                        modifier = Modifier.weight(1f),
                        textAlign = androidx.compose.ui.text.style.TextAlign.Center
                    )
                }
            }
        }
    }
}

@Composable
context(ctx: LocationECtx)
private fun TimeRangeRow(range: MessageBody.Config.OpeningHour) {
    Row(
        verticalAlignment = Alignment.CenterVertically,
        modifier = Modifier.padding(vertical = 4.dp)
    ) {
        TimeField(range.from) { ctx.action.updateTime(range, from = it) }
        Text(
            stringResource(Res.string.ui_locations_edit_range_to),
            fontSize = 10.sp,
            color = MaterialTheme.colorScheme.onSurfaceVariant,
            modifier = Modifier.padding(horizontal = 8.dp)
        )
        TimeField(range.to) { ctx.action.updateTime(range, to = it) }
        Spacer(Modifier.width(8.dp))
        IconButton(
            onClick = { ctx.action.removeRange(range) },
            modifier = Modifier.size(36.dp)
        ) {
            Icon(
                imageVector = Svg.DeleteOutline,
                contentDescription = stringResource(Res.string.ui_locations_edit_remove_range),
                tint = MaterialTheme.colorScheme.error,
                modifier = Modifier.size(24.dp)
            )
        }
    }
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
private fun TimeField(time: LocalTime, onValueChange: (LocalTime) -> Unit) {
    var showDialog by remember { mutableStateOf(false) }

    val textValue = remember(time) {
        "${time.hour.toString().padStart(2, '0')}:${time.minute.toString().padStart(2, '0')}"
    }

    OutlinedButton(
        onClick = { showDialog = true },
        modifier = Modifier.width(90.dp).height(48.dp),
        shape = RoundedCornerShape(4.dp),
        border = BorderStroke(1.dp, MaterialTheme.colorScheme.outlineVariant),
        contentPadding = PaddingValues(horizontal = 8.dp, vertical = 4.dp),
        colors = ButtonDefaults.outlinedButtonColors(
            contentColor = MaterialTheme.colorScheme.onSurface
        )
    ) {
        Text(
            text = textValue,
            style = MaterialTheme.typography.bodySmall.copy(
                fontWeight = FontWeight.Bold
            )
        )
    }

    if (showDialog) {
        val state = rememberTimePickerState(
            initialHour = time.hour,
            initialMinute = time.minute,
            is24Hour = true
        )

        AlertDialog(
            onDismissRequest = { showDialog = false },
            confirmButton = {
                TextButton(onClick = {
                    onValueChange(LocalTime(state.hour, state.minute))
                    showDialog = false
                }) {
                    Text(stringResource(Res.string.dialog_ok))
                }
            },
            dismissButton = {
                TextButton(onClick = { showDialog = false }) {
                    Text(stringResource(Res.string.dialog_cancel))
                }
            },
            title = {
                Text(
                    text = "SELECT TIME",
                    style = MaterialTheme.typography.titleSmall,
                    fontWeight = FontWeight.Bold
                )
            },
            text = {
                Box(
                    modifier = Modifier.fillMaxWidth(),
                    contentAlignment = Alignment.Center
                ) {
                    TimePicker(state = state)
                }
            }
        )
    }
}